import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Modal,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconExternalLink,
  IconRefresh,
  IconShieldLock,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

/**
 * Data deletion audit (Meta App Review compliance — M14, M20).
 *
 * Lists every Meta `data_deletion` / `deauthorize` signed_request callback
 * the platform has received, plus the resulting hard-delete status. Failed
 * rows can be retried; completed rows can be inspected via the public
 * confirmation URL Meta's reviewer uses.
 */
type StatusFilter = 'all' | 'received' | 'in_progress' | 'completed' | 'failed';

const STATUS_COLOR: Record<string, string> = {
  received: 'blue',
  in_progress: 'yellow',
  completed: 'green',
  failed: 'red',
};

function DataDeletionPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const list = trpc.admin.dataDeletion.list.useQuery({
    status: statusFilter === 'all' ? undefined : statusFilter,
    take: 100,
  });

  const retry = trpc.admin.dataDeletion.retry.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Retried — status now: ${r.status}`,
        color: r.status === 'completed' ? 'green' : 'orange',
      });
      void utils.admin.dataDeletion.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const apiBase =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${window.location.host
          .replace(':5173', ':3000')
          .replace(':5174', ':3000')}`
      : '';

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group gap="sm">
          <IconShieldLock size={22} />
          <Title order={2}>Data deletion requests</Title>
        </Group>
        <Text c="dimmed" size="sm">
          Every Meta-initiated deletion callback (data_deletion, deauthorize) and user self-service
          deletion lands here. Required for Meta App Review compliance — Meta&apos;s reviewer can
          verify a deletion via the public confirmation URL.
        </Text>

        <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
          Meta posts <Code>signed_request</Code> to <Code>{apiBase}/api/meta/data-deletion</Code>.
          Each callback returns a <Code>confirmation_code</Code> that resolves to a public status
          page Meta&apos;s reviewer can hit at{' '}
          <Code>{apiBase}/api/meta/deletion-status/&#123;code&#125;</Code>.
        </Alert>

        <Group justify="space-between">
          <SegmentedControl
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            data={[
              { value: 'all', label: 'All' },
              { value: 'received', label: 'Received' },
              { value: 'in_progress', label: 'In progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'failed', label: 'Failed' },
            ]}
          />
          <Button
            variant="default"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={() => void list.refetch()}
            loading={list.isFetching}
          >
            Refresh
          </Button>
        </Group>

        <Card p={0}>
          {list.isPending ? (
            <Skeleton height={280} m="md" />
          ) : list.isError ? (
            <Text c="red" p="md" size="sm">
              {list.error.message}
            </Text>
          ) : (list.data?.items ?? []).length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              No deletion requests yet.
            </Text>
          ) : (
            <Table striped withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>ID</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Confirmation code</Table.Th>
                  <Table.Th>FB user id</Table.Th>
                  <Table.Th>Vendor</Table.Th>
                  <Table.Th>Received</Table.Th>
                  <Table.Th>Completed</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(list.data?.items ?? []).map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="xs" ff="monospace">
                        #{row.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOR[row.status] ?? 'gray'} variant="light" size="sm">
                        {row.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace" truncate>
                        {row.confirmationCode}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" ff="monospace" truncate>
                        {row.facebookUserId ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{row.vendorId ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(row.receivedAt).toLocaleString()}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {row.completedAt ? new Date(row.completedAt).toLocaleString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="Open public status page (what Meta's reviewer sees)">
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            component="a"
                            href={`${apiBase}/api/meta/deletion-status/${row.confirmationCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconExternalLink size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Inspect notes / payload">
                          <Button
                            size="xs"
                            variant="subtle"
                            onClick={() =>
                              setOpenRow(
                                openRow === row.confirmationCode ? null : row.confirmationCode,
                              )
                            }
                          >
                            Details
                          </Button>
                        </Tooltip>
                        {(row.status === 'failed' || row.status === 'in_progress') && (
                          <Tooltip label="Re-run the hard-delete for this row">
                            <Button
                              size="xs"
                              variant="light"
                              color="orange"
                              leftSection={<IconRefresh size={12} />}
                              loading={
                                retry.isPending && retry.variables?.deletionRequestId === row.id
                              }
                              onClick={() => retry.mutate({ deletionRequestId: row.id })}
                            >
                              Retry
                            </Button>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal
        opened={!!openRow}
        onClose={() => setOpenRow(null)}
        title="Deletion request details"
        size="lg"
        centered
      >
        {(() => {
          const row = (list.data?.items ?? []).find((r) => r.confirmationCode === openRow);
          if (!row) return null;
          return (
            <Stack gap="sm">
              <Group gap="xs">
                <Badge color={STATUS_COLOR[row.status] ?? 'gray'} variant="light">
                  {row.status}
                </Badge>
                <Text size="xs" ff="monospace">
                  #{row.id}
                </Text>
              </Group>
              <Text size="sm">
                <b>Confirmation code:</b> <Code>{row.confirmationCode}</Code>
              </Text>
              <Text size="sm">
                <b>Facebook user id:</b> <Code>{row.facebookUserId ?? '—'}</Code>
              </Text>
              <Text size="sm">
                <b>Vendor id:</b> {row.vendorId ?? '—'}
              </Text>
              <Text size="sm">
                <b>Received at:</b> {new Date(row.receivedAt).toLocaleString()}
              </Text>
              <Text size="sm">
                <b>Completed at:</b>{' '}
                {row.completedAt ? new Date(row.completedAt).toLocaleString() : '—'}
              </Text>
              <Text size="sm" fw={600} mt="xs">
                Notes
              </Text>
              <ScrollArea h={200}>
                <Code block style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {row.notes ? prettyJson(row.notes) : '(empty)'}
                </Code>
              </ScrollArea>
            </Stack>
          );
        })()}
      </Modal>
    </motion.div>
  );
}

function prettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}

export const Route = createFileRoute('/central/data-deletion')({
  component: DataDeletionPage,
});
